<template>
  <div class="mcard-wrap">
    <article class="mcard">
      <div class="mcard__body">
        <div class="mcard__head">
          <span class="mcard__avatar">
            <img v-if="item.avatar" :src="item.avatar" :alt="item.name" />
            <span v-else>{{
              item.name ? item.name.slice(0, 2).toUpperCase() : "PE"
            }}</span>
          </span>
          <div class="mcard__id">
            <div class="mcard__eyebrow">{{ $t("partnerCard.eyebrow") }}</div>
            <div class="mcard__name">{{ item.name }}</div>
            <div
              v-if="item.rankingMedal || (item.badges || []).length"
              class="mcard__badges"
            >
              <span
                v-if="item.rankingMedal"
                class="mcard__medal"
                :title="
                  $t('partnerCard.rankingMedal', { n: item.rankingMedal })
                "
              >
                <v-icon
                  size="18"
                  :color="item.rankingMedal === 1 ? '#E0A800' : '#9AA3AD'"
                  >mdi-medal</v-icon
                >
                {{ $t("partnerCard.rankingMedal", { n: item.rankingMedal }) }}
              </span>
              <badge-icon
                v-for="badge in item.badges || []"
                :key="badge.code"
                :badge="badge"
                :size="24"
              />
            </div>
          </div>
        </div>

        <div v-if="(item.certificates || []).length" class="mcard__block">
          <div class="mcard__block-label">
            {{ $t("partnerCard.certificates") }}
          </div>
          <div class="mcard__chips">
            <span
              v-for="(cert, i) in item.certificates"
              :key="`cert-${i}`"
              class="mcard__chip mcard__chip--soft"
              >{{ cert }}</span
            >
          </div>
        </div>

        <!-- Card 86ajbxjn7: o card do professor informa "ser individual, a
             frequência, o valor e os horários disponíveis". Esta seção do
             match só traz planos individuais, então a modalidade é certa; as
             frequências são as dos planos que servem a este aluno (a API já
             filtrou), não um chute do primeiro plano. -->
        <div class="mcard__block">
          <div class="mcard__block-label">
            {{ $t("partnerCard.individualLessons") }}
          </div>
          <div class="mcard__chips">
            <span class="mcard__chip mcard__chip--soft">{{
              $t("partnerCard.individual")
            }}</span>
            <span
              v-for="label in planFrequencies"
              :key="`freq-${label}`"
              class="mcard__chip mcard__chip--soft"
              >{{ label }}</span
            >
            <span
              v-if="!planFrequencies.length"
              class="mcard__chip mcard__chip--soft"
              >{{ $t("partnerCard.frequencyTbd") }}</span
            >
          </div>
        </div>

        <!-- Retorno de 14/07: dias e horário repetiam os chips abaixo, que
             dizem o mesmo com mais precisão. -->
        <div class="mcard__block">
          <div class="mcard__block-label">
            {{ $t("partnerCard.availability") }}
          </div>
          <div class="mcard__chips">
            <span
              v-for="(hour, index) in previewAvailability"
              :key="`${hour.weekDay}-${hour.from}-${index}`"
              class="mcard__chip"
            >
              {{ displayWeekDayLabel(hour.weekDay) }} · {{ hour.from }}–{{
                hour.to
              }}
            </span>
          </div>
        </div>

        <button type="button" class="mcard__more" @click="openInfo">
          {{ $t("partnerCard.moreInfo", { name: firstName }) }}
        </button>
      </div>

      <div class="mcard__foot">
        <div class="mcard__price">
          <span class="mcard__price-label">{{
            teachingPlans.length > 1
              ? $t("partnerCard.feeFrom")
              : $t("partnerCard.fee")
          }}</span>
          <span class="mcard__price-value">{{
            teachingPlans.length > 0
              ? teachingPlans[0].price
              : $t("partnerCard.toBeAgreed")
          }}</span>
        </div>
        <div class="mcard__actions">
          <!-- Card 86aje36nz: uma experimental por vez. Card 86akp2bkg:
               estendido a qualquer solicitação em aberto. A API recusa de
               qualquer forma; aqui o aluno entende por que, em vez de
               tomar um erro depois de preencher o agendamento. -->
          <button
            type="button"
            class="mcard__btn mcard__btn--ghost"
            :disabled="hasPendingTrial || blocked || trialDone"
            :title="trialTitleHint"
            @click="matchUs(item, true)"
          >
            {{ $t("partnerCard.trial") }}
          </button>
          <button
            type="button"
            class="mcard__btn"
            :disabled="blocked"
            :title="blockedTitle"
            @click="matchUs(item, false)"
          >
            {{ $t("partnerCard.link") }}
          </button>
        </div>
      </div>
    </article>

    <trial-schedule-dialog
      ref="trialDialog"
      :title="trialTitle"
      :availability="selected ? selected.available : []"
      @confirmed="requestTrial"
    />

    <confirm-dialog
      ref="confirmDialog"
      :title="dialogTitle"
      :text="dialogText"
      :item="selected"
      :limit="dialogLimit"
      :content="dialogContent"
      @confirmed="requestJoin"
      @cancel="cancel"
    />

    <transition name="pmodal-fade">
      <div
        v-if="infoOpen"
        class="pmodal"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('partnerCard.about', { name: item.name })"
        @click.self="closeInfo"
      >
        <div class="pmodal__card">
          <button
            type="button"
            class="pmodal__close"
            :aria-label="$t('common.close')"
            @click="closeInfo"
          >
            ✕
          </button>

          <div class="pmodal__head">
            <span class="pmodal__avatar">
              <img v-if="item.avatar" :src="item.avatar" :alt="item.name" />
              <span v-else>{{
                item.name ? item.name.slice(0, 2).toUpperCase() : "PE"
              }}</span>
            </span>
            <div class="pmodal__id">
              <div class="mcard__eyebrow">{{ $t("partnerCard.teacher") }}</div>
              <div class="pmodal__name">{{ item.name }}</div>
              <div class="pmodal__stats">
                <span class="pmodal__stat">
                  <i18n
                    path="partnerCard.students"
                    :plural="item.studentsCount || 0"
                    tag="span"
                  >
                    <template #count>
                      <b>{{ item.studentsCount || 0 }}</b>
                    </template>
                  </i18n>
                </span>
                <span v-if="memberSinceLabel" class="pmodal__stat">
                  <i18n path="partnerCard.since" tag="span">
                    <template #date>
                      <b>{{ memberSinceLabel }}</b>
                    </template>
                  </i18n>
                </span>
              </div>
            </div>
          </div>

          <p v-if="item.bio" class="pmodal__bio">{{ item.bio }}</p>
          <p v-else class="pmodal__bio pmodal__bio--empty">
            {{ $t("partnerCard.noBio") }}
          </p>

          <div v-if="(item.certificates || []).length" class="pmodal__section">
            <div class="mcard__block-label">
              {{ $t("partnerCard.certificates") }}
            </div>
            <div class="mcard__chips">
              <span
                v-for="(cert, i) in item.certificates"
                :key="`mcert-${i}`"
                class="mcard__chip mcard__chip--soft"
                >{{ cert }}</span
              >
            </div>
          </div>

          <!-- Card 86aje33pt: os planos de ensino que o professor oferece. O
               card resume no menor valor; aqui o aluno vê o que cada plano
               inclui antes de escolher. -->
          <div v-if="teachingPlans.length" class="pmodal__section">
            <div class="mcard__block-label">{{ $t("partnerCard.plans") }}</div>
            <div class="pmodal__plans">
              <div
                v-for="plan in teachingPlans"
                :key="plan.id"
                class="pmodal__plan"
              >
                <div class="pmodal__plan-head">
                  <span class="pmodal__plan-name">{{ plan.label }}</span>
                  <span class="pmodal__plan-price">{{ plan.price }}</span>
                </div>
                <span v-if="plan.frequency" class="pmodal__plan-meta">{{
                  plan.frequency
                }}</span>
              </div>
            </div>
          </div>

          <div v-if="hasExperience" class="pmodal__section">
            <div class="mcard__block-label">
              {{ $t("partnerCard.experience") }}
            </div>
            <div class="pmodal__facts">
              <div v-if="experienceLabel" class="pmodal__fact">
                <span class="mcard__k">{{
                  $t("partnerCard.yearsOfExperience")
                }}</span>
                <span class="mcard__v">{{ experienceLabel }}</span>
              </div>
              <div v-if="formationLabel" class="pmodal__fact">
                <span class="mcard__k">{{ $t("partnerCard.formation") }}</span>
                <span class="mcard__v">{{ formationLabel }}</span>
              </div>
            </div>

            <!-- Campos multivalorados viram chips: sao varias respostas, e a
                 mesma marcacao dos certificados mantem o modal coerente. -->
            <div v-if="specialities.length" class="pmodal__section">
              <div class="mcard__block-label">
                {{ $t("partnerCard.specialities") }}
              </div>
              <div class="mcard__chips">
                <span
                  v-for="(label, i) in specialities"
                  :key="`spec-${i}`"
                  class="mcard__chip mcard__chip--soft"
                  >{{ label }}</span
                >
              </div>
            </div>

            <div v-if="institutions.length" class="pmodal__section">
              <div class="mcard__block-label">
                {{ $t("partnerCard.institutions") }}
              </div>
              <div class="mcard__chips">
                <span
                  v-for="(label, i) in institutions"
                  :key="`inst-${i}`"
                  class="mcard__chip mcard__chip--soft"
                  >{{ label }}</span
                >
              </div>
            </div>

            <div v-if="teachingAbroad.length" class="pmodal__section">
              <div class="mcard__block-label">
                {{ $t("partnerCard.abroad") }}
              </div>
              <div class="mcard__chips">
                <span
                  v-for="(label, i) in teachingAbroad"
                  :key="`abroad-${i}`"
                  class="mcard__chip mcard__chip--soft"
                  >{{ label }}</span
                >
              </div>
            </div>

            <div v-if="exp.differential" class="pmodal__para">
              <span class="mcard__k">{{ $t("partnerCard.differential") }}</span>
              <p>{{ exp.differential }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  academicFormationLabel,
  institutionLabels,
  specialityLabels,
  teachingAbroadLabels,
  yearsOfExperienceLabel,
} from "@/shared/partnerPresentation";
import {
  displayGroupFormat,
  displayPlanFrequency,
  WEEK_DAY_ITEMS,
} from "@/shared/classPresentation";
import TrialScheduleDialog from "@/components/connect/partner/TrialScheduleDialog.vue";
import BadgeIcon from "@/components/coins/BadgeIcon.vue";
import { formatBRL, languageTag } from "@/plugins/i18n";

export default {
  name: "MatchedPartnerCard",
  components: { TrialScheduleDialog, BadgeIcon },
  props: {
    item: {
      type: Object,
    },
    weekDay: {
      type: Number,
    },
    start: {
      type: String,
    },
    limit: {
      type: Number,
    },
    // O aluno já tem uma experimental em aberto (card 86aje36nz).
    hasPendingTrial: {
      type: Boolean,
      default: false,
    },
    // Card 86akp2bkg: o aluno já tem uma solicitação aguardando resposta
    // (entrada em turma, turma nova ou experimental).
    blocked: {
      type: Boolean,
      default: false,
    },
    // Retorno de 30/09 (card 86aje36nz): uma experimental por professor.
    trialDone: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    trialTitleHint() {
      if (!this.blocked && this.trialDone) {
        return this.$t("partnerCard.trialDone");
      }
      return this.blockedTitle;
    },
    blockedTitle() {
      if (this.blocked) {
        return this.$t("common.pendingRequestBlock");
      }
      return this.hasPendingTrial ? this.$t("partnerCard.trialPending") : "";
    },
    // Títulos dos diálogos montados no render: acompanham o idioma.
    trialTitle() {
      return this.selected
        ? this.$t("partnerCard.trialTitle", { name: this.selected.name })
        : "";
    },
    dialogTitle() {
      return this.selected && !this.experimentalRequest
        ? this.$t("partnerCard.linkTitle", { name: this.selected.name })
        : "";
    },
    dialogText() {
      return this.selected && !this.experimentalRequest
        ? this.$t("partnerCard.linkText")
        : "";
    },
    // Card 86aje33pt. Os planos vêm do match já filtrados pelo que serve a este
    // aluno; aqui só viram texto legível, do mais barato para o mais caro.
    teachingPlans() {
      return [...(this.item.plans || [])]
        .map((plan) => {
          const value = Number(plan.finalValue ?? plan.value ?? 0);

          return {
            id: plan.id,
            value,
            label: displayGroupFormat(plan.format),
            price: value ? formatBRL(value) : this.$t("partnerCard.toBeAgreed"),
            frequency: plan.frequency
              ? displayPlanFrequency(plan.frequency)
              : null,
          };
        })
        .sort((left, right) => left.value - right.value);
    },
    planFrequencies() {
      const order = [...(this.item.plans || [])]
        .map((plan) => +plan.frequency)
        .filter((value) => value > 0)
        .sort((left, right) => left - right);

      return [...new Set(order)].map((value) => displayPlanFrequency(value));
    },
    // A experimental é uma aula só, e quem escolhe o horário é o professor
    // dentre os que o aluno marcou — exigir a frequência semanal do plano aqui
    // não faz sentido. O vínculo continua seguindo o `limit` que a API calcula.
    dialogLimit() {
      return this.experimentalRequest ? 1 : this.limit;
    },
    previewAvailability() {
      return Array.isArray(this.item.available)
        ? this.item.available.slice(0, 4)
        : [];
    },
    firstName() {
      return (
        String(this.item.name || "")
          .trim()
          .split(/\s+/)[0] || this.$t("partnerCard.defaultName")
      );
    },
    exp() {
      return this.item.experience || {};
    },
    hasExperience() {
      return !!(
        this.experienceLabel ||
        this.formationLabel ||
        this.specialities.length ||
        this.institutions.length ||
        this.teachingAbroad.length ||
        this.exp.differential
      );
    },
    // A API devolve o codigo do enum, nao a quantidade de anos: `3` e
    // "6-10 anos". Imprimir o numero cru enganava o aluno.
    experienceLabel() {
      return yearsOfExperienceLabel(this.exp.yearsOfExperience);
    },
    formationLabel() {
      return academicFormationLabel(this.exp.academicFormation);
    },
    specialities() {
      return specialityLabels(this.exp.speciality);
    },
    institutions() {
      return institutionLabels(this.exp.institutions);
    },
    teachingAbroad() {
      return teachingAbroadLabels(this.exp.teachingAbroad);
    },
    memberSinceLabel() {
      if (!this.item.memberSince) return "";
      const date = new Date(this.item.memberSince);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString(languageTag(), {
        month: "long",
        year: "numeric",
      });
    },
  },
  data() {
    return {
      infoOpen: false,
      experimentalRequest: false,
      dialogContent: null,
      selected: null,
      weekDaysItems: WEEK_DAY_ITEMS,
    };
  },
  methods: {
    matchUs(item, experimental = false) {
      this.selected = item;
      this.experimentalRequest = experimental;
      this.dialogContent = item.available;

      // A experimental é um encontro único e datado: o aluno escolhe dia e
      // hora num calendário, não uma lista de dias da semana soltos.
      if (experimental) {
        this.$refs.trialDialog.open();
        return;
      }

      this.$refs.confirmDialog.open();
    },
    // O diálogo de calendário devolve um único horário datado.
    //
    // Mesmo contrato do "Vincular ao professor" (ConfirmDialog): o item do
    // professor, com `id`, mais `hours`. Quem escuta `requestJoin`
    // (MatchExplorer e a página de turmas) monta o pedido a partir de
    // `item.id`; emitir `partnerId` no lugar fazia o pedido sair sem professor
    // e a API recusava com 400 (card 86aje36nz, retorno de 15/09).
    requestTrial(hours) {
      // Retorno de 22/09: o diálogo ficava aberto depois de solicitar, como se
      // o pedido não tivesse saído.
      this.$refs.trialDialog.close();
      this.$emit("requestJoin", {
        ...this.selected,
        hours,
        experimental: true,
      });
    },
    displayWeekDayLabel(day) {
      return (
        this.weekDaysItems.find((item) => +item.value === +day)?.text || "-"
      );
    },

    requestJoin(payload) {
      this.$emit("requestJoin", {
        ...payload,
        experimental: this.experimentalRequest,
      });
    },
    cancel() {
      this.selected = null;
    },
    openInfo() {
      this.infoOpen = true;
    },
    closeInfo() {
      this.infoOpen = false;
    },
  },
};
</script>

<style lang="css" scoped>
.mcard-wrap {
  height: 100%;
}

.mcard {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  height: 100%;
  min-height: 440px;
  display: flex;
  flex-direction: column;
  padding: 22px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 15px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.mcard:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 46px -34px rgba(232, 39, 111, 0.5);
}

.mcard__body {
  flex: 1 1 auto;
}

.mcard__head {
  display: flex;
  align-items: center;
  gap: 14px;
}
.mcard__avatar {
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #ff5595),
    var(--dk-pink, #e8276f)
  );
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
}
.mcard__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mcard__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pink);
}
.mcard__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.mcard__medal {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--dk-muted, #6b6580);
}

.mcard__name {
  margin-top: 2px;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.mcard__block {
  margin-top: 16px;
}
.mcard__block-label,
.mcard__k {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}

/* .mcard__k / .mcard__v continuam em uso pelos pares do modal. */
.mcard__v {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--ink);
}

.mcard__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.mcard__chip {
  padding: 4px 11px;
  border-radius: 999px;
  background: rgba(232, 39, 111, 0.08);
  color: var(--dk-pink-deep, #be185d);
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.mcard__chip--soft {
  background: var(--paper);
  border: 1px solid var(--line);
  color: var(--ink);
}

.mcard__foot {
  margin-top: auto;
  padding-top: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.mcard__price {
  display: flex;
  flex-direction: column;
}
.mcard__price-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}
.mcard__price-value {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--pink);
}
.mcard__btn {
  flex: 0 0 auto;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #e8276f),
    var(--dk-pink-deep, #b51f57)
  );
  color: #fff;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.mcard__btn:hover {
  filter: brightness(1.06);
}
.mcard__btn:focus-visible {
  outline: 2px solid var(--pink);
  outline-offset: 2px;
}
.mcard__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
  text-align: center;
}
.mcard__btn--ghost {
  background: transparent;
  color: var(--dk-pink-deep, #be185d);
  border: 1px solid rgba(232, 39, 111, 0.4);
}
.mcard__btn--ghost:hover {
  filter: none;
  background: rgba(232, 39, 111, 0.06);
}
.mcard__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  filter: none;
}

/* botao "mais informacoes" */
.mcard__more {
  margin-top: 16px;
  width: 100%;
  padding: 9px 14px;
  border: 1px dashed rgba(232, 39, 111, 0.35);
  border-radius: 12px;
  background: transparent;
  color: var(--dk-pink-deep, #be185d);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.mcard__more:hover {
  background: rgba(232, 39, 111, 0.06);
  border-color: var(--pink);
}
.mcard__more:focus-visible {
  outline: 2px solid var(--pink);
  outline-offset: 2px;
}

/* modal do dossie do professor */
.pmodal {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.55);
  backdrop-filter: blur(2px);
}
.pmodal__card {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  position: relative;
  width: 100%;
  max-width: 540px;
  max-height: 88vh;
  overflow-y: auto;
  background: var(--dk-surface, #fff);
  border-radius: 18px;
  padding: 26px;
  box-shadow: 0 40px 80px -40px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.7);
}
.pmodal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--paper);
  color: var(--muted);
  font-size: 0.85rem;
  cursor: pointer;
}
.pmodal__close:hover {
  color: var(--ink);
}
.pmodal__head {
  display: flex;
  gap: 16px;
  align-items: center;
}
.pmodal__avatar {
  flex: 0 0 auto;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #ff5595),
    var(--dk-pink, #e8276f)
  );
  color: #fff;
  font-size: 1.1rem;
  font-weight: 800;
}
.pmodal__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pmodal__name {
  font-family: "Poppins", sans-serif;
  margin-top: 2px;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.15;
  color: var(--ink);
}
.pmodal__stats {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 0.82rem;
  color: var(--muted);
}
.pmodal__stat b {
  color: var(--ink);
}
.pmodal__bio {
  margin: 20px 0 4px;
  padding-left: 14px;
  border-left: 3px solid var(--pink);
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--ink);
  white-space: pre-line;
}
.pmodal__bio--empty {
  color: var(--muted);
  font-style: italic;
}
.pmodal__section {
  margin-top: 22px;
}
.pmodal__plans {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}
.pmodal__plan {
  border: 1px solid var(--dk-line, #e9e4f0);
  border-radius: 12px;
  padding: 10px 14px;
}
.pmodal__plan-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.pmodal__plan-name {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
}
.pmodal__plan-price {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--dk-pink, #e8276f);
  white-space: nowrap;
}
.pmodal__plan-meta {
  font-size: 0.78rem;
  color: var(--dk-muted, #6b6472);
}
.pmodal__facts {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.pmodal__fact {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.pmodal__para {
  margin-top: 12px;
}
.pmodal__para p {
  margin: 4px 0 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--ink);
}
.pmodal-fade-enter-active,
.pmodal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.pmodal-fade-enter,
.pmodal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 460px) {
  .pmodal__facts {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mcard {
    transition: none;
  }
  .mcard:hover {
    transform: none;
  }
  .pmodal-fade-enter-active,
  .pmodal-fade-leave-active {
    transition: none;
  }
}
</style>
